import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { complianceEnabled } from '@config/env';
import { useApiClient } from '@core/api/useApiClient';
import { adminApi } from '@core/api/adminApi';
import { queryKeys } from '@core/api/queryKeys';
import type {
  ComplianceAlertDetail, ComplianceAlertsPage, ComplianceAlertsParams, ComplianceCreateAlertRequest,
} from '@core/api/complianceTypes';

const COMPLIANCE_ROOT = ['admin', 'compliance'] as const;

/** One page of the inbox. `keepPreviousData` keeps the list on screen while a filter change resolves. */
export function useComplianceAlerts(params: ComplianceAlertsParams, options: { enabled?: boolean } = {}) {
  const api = useApiClient();
  return useQuery<ComplianceAlertsPage>({
    queryKey: queryKeys.complianceAlerts(params),
    queryFn:  () => adminApi.complianceAlerts(api, params),
    enabled:  complianceEnabled && (options.enabled ?? true),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

/** One alert's detail, fetched only while the drawer is open. */
export function useComplianceAlert(id: string | null) {
  const api = useApiClient();
  return useQuery<ComplianceAlertDetail>({
    queryKey: queryKeys.complianceAlert(id ?? ''),
    queryFn:  () => adminApi.complianceAlert(api, id!),
    enabled:  complianceEnabled && !!id,
  });
}

/**
 * Every open alert (untouched or in hand), the endpoint's page maximum: one query, polled every minute, shared by the
 * P1 strip, the tab badge and the overview's queue (the same key, so one request serves all three on any page).
 */
export const OPEN_ALERTS: ComplianceAlertsParams = { status: 'open', page: 1, pageSize: 200 };

export function useOpenAlerts(enabled = true) {
  return useComplianceAlerts(OPEN_ALERTS, { enabled });
}

/**
 * The open-P1 count for the strip on every page. `openP1` is store-wide (no filter applies to it). `enabled: false`
 * (the backend reports the console off) skips the request entirely.
 */
export function useOpenP1Count(enabled = true): number {
  const { data } = useOpenAlerts(enabled);
  return data?.openP1 ?? 0;
}

type AlertAction = 'acknowledge' | 'resolve' | 'reopen' | 'note';

const ACTION_DONE: Record<AlertAction, string> = {
  acknowledge: 'Alert acknowledged',
  resolve:     'Alert resolved',
  reopen:      'Alert reopened',
  note:        'Note added',
};

/**
 * The four status actions on one alert. Each appends an event server-side (the row itself never
 * changes) and refreshes the whole console, because the overview header and the P1 strip count the
 * same rows. The note is required by the contract on resolve and reopen; the form enforces it.
 */
export function useAlertAction(id: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, note }: { action: AlertAction; note?: string }) => {
      switch (action) {
        case 'acknowledge': return adminApi.acknowledgeAlert(api, id);
        case 'resolve':     return adminApi.resolveAlert(api, id, note ?? '');
        case 'reopen':      return adminApi.reopenAlert(api, id, note ?? '');
        case 'note':        return adminApi.noteAlert(api, id, note ?? '');
      }
    },
    onSuccess: (detail, { action }) => {
      toast.success(ACTION_DONE[action]);
      qc.setQueryData(queryKeys.complianceAlert(id), detail);
      void qc.invalidateQueries({ queryKey: COMPLIANCE_ROOT });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'The alert could not be updated');
    },
  });
}

/** Raise a manual alert or record the monthly escalation. Refreshes the console. */
export function useCreateAlert() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ComplianceCreateAlertRequest) => adminApi.createAlert(api, body),
    onSuccess: (detail) => {
      toast.success(detail.kind === 'audit-escalation' ? 'Escalation recorded' : 'Alert raised');
      void qc.invalidateQueries({ queryKey: COMPLIANCE_ROOT });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'The alert could not be raised');
    },
  });
}
